import { ToolItem } from '../types';

export interface UnitConversionToolItem extends ToolItem {
  unitCategory: 'common-units' | 'engineering-physics' | 'electrical-digital' | 'design-media' | 'lifestyle-specialty';
  unitCategoryLabel: string;
  iconBgColor: string;
  iconTextColor: string;
  iconName: string;
}

export const UNIT_CATEGORIES_TABS = [
  { id: 'all', label: 'All Unit Converters', icon: 'Ruler' },
  { id: 'common-units', label: 'Common Units (Length, Weight...)', icon: 'Scale' },
  { id: 'engineering-physics', label: 'Physics & Engineering', icon: 'Zap' },
  { id: 'electrical-digital', label: 'Electrical & Data', icon: 'Cpu' },
  { id: 'design-media', label: 'Design, Display & Print', icon: 'Layout' },
  { id: 'lifestyle-specialty', label: 'Lifestyle & Science', icon: 'Compass' }
];

export const UNIT_CONVERSION_TOOLS_DATA: UnitConversionToolItem[] = [
  // --- COMMON UNITS (10 Tools) ---
  {
    id: 'length-converter',
    name: 'Length Converter',
    description: 'Convert meters, kilometers, feet, inches, miles, nautical miles, and yards.',
    unitCategory: 'common-units',
    unitCategoryLabel: 'Common Units',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'Ruler',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'weight-converter',
    name: 'Weight Converter',
    description: 'Convert kilograms, grams, pounds, ounces, tons, milligrams, and stone.',
    unitCategory: 'common-units',
    unitCategoryLabel: 'Common Units',
    iconBgColor: 'bg-cyan-500/15',
    iconTextColor: 'text-cyan-500',
    iconName: 'Scale',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'temperature-converter',
    name: 'Temperature Converter',
    description: 'Convert Celsius, Fahrenheit, Kelvin, and Rankine temperature scales.',
    unitCategory: 'common-units',
    unitCategoryLabel: 'Common Units',
    iconBgColor: 'bg-rose-500/15',
    iconTextColor: 'text-rose-500',
    iconName: 'Thermometer',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'area-converter',
    name: 'Area Converter',
    description: 'Convert square meters, square feet, acres, hectares, and square kilometers.',
    unitCategory: 'common-units',
    unitCategoryLabel: 'Common Units',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'Maximize2',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'volume-converter',
    name: 'Volume Converter',
    description: 'Convert liters, milliliters, cubic meters, gallons, quarts, and fluid ounces.',
    unitCategory: 'common-units',
    unitCategoryLabel: 'Common Units',
    iconBgColor: 'bg-sky-500/15',
    iconTextColor: 'text-sky-500',
    iconName: 'Box',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'speed-converter',
    name: 'Speed Converter',
    description: 'Convert km/h, mph, knots, meters per second, and speed of light.',
    unitCategory: 'common-units',
    unitCategoryLabel: 'Common Units',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'Gauge',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'time-converter',
    name: 'Time Converter',
    description: 'Convert seconds, minutes, hours, days, weeks, months, years, and milliseconds.',
    unitCategory: 'common-units',
    unitCategoryLabel: 'Common Units',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'Clock',
    actionType: 'generic'
  },
  {
    id: 'angle-converter',
    name: 'Angle Converter',
    description: 'Convert degrees, radians, gradians, arcseconds, arcminutes, and turns.',
    unitCategory: 'common-units',
    unitCategoryLabel: 'Common Units',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'Compass',
    actionType: 'generic'
  },
  {
    id: 'density-converter',
    name: 'Density Converter',
    description: 'Convert kg/m³, g/cm³, lbs/ft³, lbs/gal, and specific gravity units.',
    unitCategory: 'common-units',
    unitCategoryLabel: 'Common Units',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'Layers',
    actionType: 'generic'
  },
  {
    id: 'temperature-difference-converter',
    name: 'Temperature Difference Converter',
    description: 'Convert relative temperature delta differences between °C, °F, and Kelvin.',
    unitCategory: 'common-units',
    unitCategoryLabel: 'Common Units',
    iconBgColor: 'bg-orange-500/15',
    iconTextColor: 'text-orange-500',
    iconName: 'ThermometerSun',
    actionType: 'generic'
  },

  // --- PHYSICS & ENGINEERING (9 Tools) ---
  {
    id: 'pressure-converter',
    name: 'Pressure Converter',
    description: 'Convert pascals, bar, PSI, atmospheres, mmHg, and Torr pressure measurements.',
    unitCategory: 'engineering-physics',
    unitCategoryLabel: 'Physics & Engineering',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'Activity',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'energy-converter',
    name: 'Energy Converter',
    description: 'Convert joules, kilojoules, calories, kilocalories, BTU, electronvolts, and kWh.',
    unitCategory: 'engineering-physics',
    unitCategoryLabel: 'Physics & Engineering',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'Zap',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'power-converter',
    name: 'Power Converter',
    description: 'Convert watts, kilowatts, horsepower, metric HP, and BTU per hour.',
    unitCategory: 'engineering-physics',
    unitCategoryLabel: 'Physics & Engineering',
    iconBgColor: 'bg-orange-500/15',
    iconTextColor: 'text-orange-500',
    iconName: 'Flame',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'fuel-economy-converter',
    name: 'Fuel Economy Converter',
    description: 'Convert Miles Per Gallon (MPG US/UK) to kilometers per liter (km/L).',
    unitCategory: 'engineering-physics',
    unitCategoryLabel: 'Physics & Engineering',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'Fuel',
    actionType: 'generic'
  },
  {
    id: 'fuel-consumption-converter',
    name: 'Fuel Consumption Converter',
    description: 'Convert Liters per 100km (L/100km) to MPG and metric fuel consumption rates.',
    unitCategory: 'engineering-physics',
    unitCategoryLabel: 'Physics & Engineering',
    iconBgColor: 'bg-green-500/15',
    iconTextColor: 'text-green-500',
    iconName: 'Droplet',
    actionType: 'generic'
  },
  {
    id: 'torque-converter',
    name: 'Torque Converter',
    description: 'Convert Newton-meters (N·m), foot-pounds (ft-lb), inch-pounds, and kgf·m.',
    unitCategory: 'engineering-physics',
    unitCategoryLabel: 'Physics & Engineering',
    iconBgColor: 'bg-violet-500/15',
    iconTextColor: 'text-violet-500',
    iconName: 'RotateCw',
    actionType: 'generic'
  },
  {
    id: 'force-converter',
    name: 'Force Converter',
    description: 'Convert newtons, kilonewtons, pound-force (lbf), dyne, and kilogram-force.',
    unitCategory: 'engineering-physics',
    unitCategoryLabel: 'Physics & Engineering',
    iconBgColor: 'bg-rose-500/15',
    iconTextColor: 'text-rose-500',
    iconName: 'Move',
    actionType: 'generic'
  },
  {
    id: 'acceleration-converter',
    name: 'Acceleration Converter',
    description: 'Convert meters per second squared (m/s²), standard gravity (g-force), and ft/s².',
    unitCategory: 'engineering-physics',
    unitCategoryLabel: 'Physics & Engineering',
    iconBgColor: 'bg-yellow-500/15',
    iconTextColor: 'text-yellow-500',
    iconName: 'TrendingUp',
    actionType: 'generic'
  },
  {
    id: 'flow-rate-converter',
    name: 'Flow Rate Converter',
    description: 'Convert volumetric flow rates: liters per minute, m³/h, gallons per minute (GPM).',
    unitCategory: 'engineering-physics',
    unitCategoryLabel: 'Physics & Engineering',
    iconBgColor: 'bg-cyan-500/15',
    iconTextColor: 'text-cyan-500',
    iconName: 'Wind',
    actionType: 'generic'
  },

  // --- ELECTRICAL & DATA (8 Tools) ---
  {
    id: 'frequency-converter',
    name: 'Frequency Converter',
    description: 'Convert hertz (Hz), kilohertz (kHz), megahertz (MHz), gigahertz (GHz), and RPM.',
    unitCategory: 'electrical-digital',
    unitCategoryLabel: 'Electrical & Data',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'Radio',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'voltage-converter',
    name: 'Voltage Converter',
    description: 'Convert volts (V), millivolts (mV), kilovolts (kV), and megavolts.',
    unitCategory: 'electrical-digital',
    unitCategoryLabel: 'Electrical & Data',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'Zap',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'current-converter',
    name: 'Current Converter',
    description: 'Convert amperes (A), milliamperes (mA), microamperes, and kiloamperes.',
    unitCategory: 'electrical-digital',
    unitCategoryLabel: 'Electrical & Data',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'Activity',
    actionType: 'generic'
  },
  {
    id: 'resistance-converter',
    name: 'Resistance Converter',
    description: 'Convert ohms (Ω), kilohms (kΩ), megohms (MΩ), and electrical conductance.',
    unitCategory: 'electrical-digital',
    unitCategoryLabel: 'Electrical & Data',
    iconBgColor: 'bg-sky-500/15',
    iconTextColor: 'text-sky-500',
    iconName: 'Cpu',
    actionType: 'generic'
  },
  {
    id: 'capacitance-converter',
    name: 'Capacitance Converter',
    description: 'Convert farads (F), microfarads (µF), nanofarads (nF), and picofarads (pF).',
    unitCategory: 'electrical-digital',
    unitCategoryLabel: 'Electrical & Data',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'Sliders',
    actionType: 'generic'
  },
  {
    id: 'inductance-converter',
    name: 'Inductance Converter',
    description: 'Convert henries (H), millihenries (mH), and microhenries (µH).',
    unitCategory: 'electrical-digital',
    unitCategoryLabel: 'Electrical & Data',
    iconBgColor: 'bg-fuchsia-500/15',
    iconTextColor: 'text-fuchsia-500',
    iconName: 'Repeat',
    actionType: 'generic'
  },
  {
    id: 'data-storage-converter',
    name: 'Data Storage Converter',
    description: 'Convert Bytes, KB, MB, GB, TB, PB, Bits, Kibibytes, and Mebibytes.',
    unitCategory: 'electrical-digital',
    unitCategoryLabel: 'Electrical & Data',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'Database',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'data-transfer-speed-converter',
    name: 'Data Transfer Speed Converter',
    description: 'Convert network transfer speeds: Mbps, Gbps, MB/s, KB/s, and Bytes per sec.',
    unitCategory: 'electrical-digital',
    unitCategoryLabel: 'Electrical & Data',
    iconBgColor: 'bg-violet-500/15',
    iconTextColor: 'text-violet-500',
    iconName: 'Wifi',
    isPopular: true,
    actionType: 'generic'
  },

  // --- DESIGN, DISPLAY & PRINT (7 Tools) ---
  {
    id: 'typography-converter',
    name: 'Typography Converter (px/rem/em)',
    description: 'Convert CSS pixels (px) to rem, em, points (pt), and percent for web design.',
    unitCategory: 'design-media',
    unitCategoryLabel: 'Design, Display & Print',
    iconBgColor: 'bg-pink-500/15',
    iconTextColor: 'text-pink-500',
    iconName: 'Type',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'paper-size-converter',
    name: 'Paper Size Converter',
    description: 'Dimensions comparison for ISO A4, A3, Letter, Legal, Tabloid, and envelope sizes.',
    unitCategory: 'design-media',
    unitCategoryLabel: 'Design, Display & Print',
    iconBgColor: 'bg-rose-500/15',
    iconTextColor: 'text-rose-500',
    iconName: 'FileText',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'color-unit-converter',
    name: 'Color Unit Converter',
    description: 'Convert color code formats between HEX, RGB, HSL, HSV, and CMYK percentages.',
    unitCategory: 'design-media',
    unitCategoryLabel: 'Design, Display & Print',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'Palette',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'digital-image-size-converter',
    name: 'Digital Image Size Converter',
    description: 'Convert image dimensions in pixels, inches, cm, and print DPI resolutions.',
    unitCategory: 'design-media',
    unitCategoryLabel: 'Design, Display & Print',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'Image',
    actionType: 'generic'
  },
  {
    id: 'resolution-converter',
    name: 'Resolution Converter',
    description: 'Compare display resolutions: 720p, 1080p, 2K, 4K UHD, and 8K dimensions.',
    unitCategory: 'design-media',
    unitCategoryLabel: 'Design, Display & Print',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'Monitor',
    actionType: 'generic'
  },
  {
    id: 'pixel-density-converter',
    name: 'Pixel Density Converter (PPI/DPI)',
    description: 'Calculate Pixels Per Inch (PPI) and Dots Per Inch (DPI) for displays and print.',
    unitCategory: 'design-media',
    unitCategoryLabel: 'Design, Display & Print',
    iconBgColor: 'bg-cyan-500/15',
    iconTextColor: 'text-cyan-500',
    iconName: 'Grid',
    actionType: 'generic'
  },
  {
    id: 'aspect-ratio-converter',
    name: 'Aspect Ratio Converter',
    description: 'Calculate and convert width/height dimensions for 16:9, 4:3, 21:9, and 1:1 ratios.',
    unitCategory: 'design-media',
    unitCategoryLabel: 'Design, Display & Print',
    iconBgColor: 'bg-sky-500/15',
    iconTextColor: 'text-sky-500',
    iconName: 'Maximize',
    isPopular: true,
    actionType: 'generic'
  },

  // --- LIFESTYLE & SCIENCE (6 Tools) ---
  {
    id: 'cooking-measurement-converter',
    name: 'Cooking Measurement Converter',
    description: 'Convert cups, tablespoons, teaspoons, fluid ounces, grams, and recipe pinches.',
    unitCategory: 'lifestyle-specialty',
    unitCategoryLabel: 'Lifestyle & Science',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'Coffee',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'shoe-size-converter',
    name: 'Shoe Size Converter',
    description: 'Compare international shoe sizes across US, UK, EU, Japan, and CM lengths.',
    unitCategory: 'lifestyle-specialty',
    unitCategoryLabel: 'Lifestyle & Science',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'Footprints',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'clothing-size-converter',
    name: 'Clothing Size Converter',
    description: 'Convert international apparel sizes for men and women across US, UK, and EU.',
    unitCategory: 'lifestyle-specialty',
    unitCategoryLabel: 'Lifestyle & Science',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'Shirt',
    actionType: 'generic'
  },
  {
    id: 'currency-converter',
    name: 'Currency Converter (Manual Rates)',
    description: 'Calculate multi-currency values using custom reference exchange rates.',
    unitCategory: 'lifestyle-specialty',
    unitCategoryLabel: 'Lifestyle & Science',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'DollarSign',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'bmi-unit-converter',
    name: 'BMI Unit Converter',
    description: 'Convert metric vs imperial height/weight inputs to Body Mass Index values.',
    unitCategory: 'lifestyle-specialty',
    unitCategoryLabel: 'Lifestyle & Science',
    iconBgColor: 'bg-rose-500/15',
    iconTextColor: 'text-rose-500',
    iconName: 'HeartPulse',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'scientific-unit-converter',
    name: 'Scientific Unit Converter',
    description: 'Convert SI prefixes (pico, nano, micro, milli, kilo, mega, giga, tera, peta).',
    unitCategory: 'lifestyle-specialty',
    unitCategoryLabel: 'Lifestyle & Science',
    iconBgColor: 'bg-violet-500/15',
    iconTextColor: 'text-violet-500',
    iconName: 'Atom',
    actionType: 'generic'
  }
];
